import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import type { SourceResponse, CitationResponse, MediaAssetResponse } from 'we-tree-api-client/v2';

import { useDomainStore } from '@/hooks/useStores';
import { sourcesApi, citationsApi } from '@/api/sourcesWrapper';
import { mediaApi } from '@/api/mediaWrapper';
import { canModifyFamily } from '@/utils/rolePermissions';
import { canModifyItem } from '@/types/access';
import ChooseMediaModal from '@/components/media/ChooseMediaModal';
import SourceCard from '@/components/sources/SourceCard';
import { CONFIDENCE_OPTIONS, type ConfidenceOption } from '@/components/sources/confidenceOptions';
import SourcesTypeFilter, {
  type SourceTypeCounts,
  type SourceTypeFilter
} from '@/components/sources/SourcesTypeFilter';
import {
  getSourceTypeLabel,
  SOURCE_TYPES,
  normalizeSourceType,
  type SourceType
} from '@/components/sources/sourceTypeMeta';
import Plus from '@/icons/Plus';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import Spinner from '@/components/shared/Spinner';
import ListResultsBar from '@/components/shared/ListResultsBar';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  FAMILY_SOURCES_TOUR_STEPS,
  FAMILY_SOURCES_TOUR_STORAGE_KEY,
  resolveFamilySourcesTourSteps
} from '@/data/family-sources-tour';

import './family-sources-screen.css';

const CITATIONS_PAGE_SIZE = 100;

function groupCitationsBySource(
  sources: SourceResponse[],
  citations: CitationResponse[]
): Record<string, CitationResponse[]> {
  const bySource: Record<string, CitationResponse[]> = Object.fromEntries(
    sources.map((s) => [s.id, [] as CitationResponse[]])
  );
  for (const citation of citations) {
    const list = bySource[citation.sourceId];
    if (list) {
      list.push(citation);
    }
  }
  return bySource;
}

const FamilySourcesScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const navigate = useNavigate();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [items, setItems] = useState<SourceResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedSourceIds, setExpandedSourceIds] = useState<Record<string, boolean>>({});
  const [citationsBySource, setCitationsBySource] = useState<Record<string, CitationResponse[]>>({});
  const [loadingCitations, setLoadingCitations] = useState<Record<string, boolean>>({});
  const [citationsPreloadLoading, setCitationsPreloadLoading] = useState(false);

  // Add source form state
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState<SourceType>('other');
  const [newUrl, setNewUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Family media for citation image choice (and selected label)
  const [mediaAssets, setMediaAssets] = useState<MediaAssetResponse[]>([]);
  const [imageOverlayMediaId, setImageOverlayMediaId] = useState<string | null>(null);

  // Add citation form state (per source)
  const [citationSourceId, setCitationSourceId] = useState<string | null>(null);
  const [citationMediaAssetId, setCitationMediaAssetId] = useState('');
  const [citationNotes, setCitationNotes] = useState('');
  const [citationDetailLocator, setCitationDetailLocator] = useState('');
  const [citationConfidence, setCitationConfidence] = useState<ConfidenceOption | ''>('');
  const [submittingCitation, setSubmittingCitation] = useState(false);

  /** 'add' = choosing media for new citation, 'edit' = for editing citation */
  const [chooseMediaContext, setChooseMediaContext] = useState<'add' | 'edit' | null>(null);

  // Edit source state
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState<SourceType>('other');
  const [editUrl, setEditUrl] = useState('');
  const [submittingEditSource, setSubmittingEditSource] = useState(false);

  // Edit citation state (citationId; sourceId comes from the citation we're editing)
  const [editingCitationId, setEditingCitationId] = useState<string | null>(null);
  const [editingCitationSourceId, setEditingCitationSourceId] = useState<string | null>(null);
  const [editCitationDetailLocator, setEditCitationDetailLocator] = useState('');
  const [editCitationNotes, setEditCitationNotes] = useState('');
  const [editCitationConfidence, setEditCitationConfidence] = useState<ConfidenceOption | ''>('');
  const [editCitationMediaAssetId, setEditCitationMediaAssetId] = useState('');
  const [submittingEditCitation, setSubmittingEditCitation] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState<SourceTypeFilter>('all');

  const loadAllCitationsForSources = useCallback(
    async (sources: SourceResponse[]) => {
      if (!familyIdParam) return;
      if (sources.length === 0) {
        setCitationsBySource({});
        setCitationsPreloadLoading(false);
        return;
      }

      setCitationsPreloadLoading(true);
      const allCitations: CitationResponse[] = [];
      let page = 1;

      while (true) {
        const result = await citationsApi.listCitations(familyIdParam, {
          page,
          pageSize: CITATIONS_PAGE_SIZE
        });
        if (!result.success || !result.data) {
          setCitationsPreloadLoading(false);
          return;
        }
        allCitations.push(...result.data.items);
        if (allCitations.length >= result.data.total || result.data.items.length < CITATIONS_PAGE_SIZE) {
          break;
        }
        page += 1;
      }

      setCitationsBySource(groupCitationsBySource(sources, allCitations));
      setCitationsPreloadLoading(false);
    },
    [familyIdParam]
  );

  const loadSources = useCallback(async () => {
    if (!familyIdParam) return;
    setIsLoading(true);
    setCitationsBySource({});
    setCitationsPreloadLoading(true);
    setErrorMessage(null);
    const result = await sourcesApi.listSources(familyIdParam, { page: 1, pageSize: 100 });
    setIsLoading(false);
    if (result.success && result.data) {
      setItems(result.data.items);
      setTotal(result.data.total);
      void loadAllCitationsForSources(result.data.items);
    } else {
      setErrorMessage(!result.success && result.error ? result.error.message : t('sources.loadFailed'));
      setItems([]);
      setTotal(0);
      setCitationsBySource({});
      setCitationsPreloadLoading(false);
    }
  }, [familyIdParam, loadAllCitationsForSources, t]);

  useEffect(() => {
    if (!familyIdParam) {
      setErrorMessage(t('errors.noFamily'));
      return;
    }
    setErrorMessage(null);
    domainStore.setSelectedFamily(familyIdParam);
  }, [domainStore, familyIdParam, t]);

  useEffect(() => {
    if (familyIdParam) {
      loadSources();
    }
  }, [familyIdParam, loadSources]);

  const loadMediaAssets = useCallback(async () => {
    if (!familyIdParam) return;
    const result = await mediaApi.searchMediaAssets(familyIdParam, { page: 1, pageSize: 100 });
    if (result.success && result.data) {
      setMediaAssets(result.data.items);
    } else {
      setMediaAssets([]);
    }
  }, [familyIdParam]);

  useEffect(() => {
    if ((citationSourceId || editingCitationId) && familyIdParam) {
      loadMediaAssets();
    }
  }, [citationSourceId, editingCitationId, familyIdParam, loadMediaAssets]);

  const loadCitationsForSource = useCallback(
    async (sourceId: string) => {
      if (!familyIdParam) return;
      setLoadingCitations((prev) => ({ ...prev, [sourceId]: true }));
      const result = await sourcesApi.listCitationsBySource(familyIdParam, sourceId, {
        page: 1,
        pageSize: 50
      });
      setLoadingCitations((prev) => ({ ...prev, [sourceId]: false }));
      if (result.success && result.data) {
        setCitationsBySource((prev) => ({ ...prev, [sourceId]: result.data!.items }));
      }
    },
    [familyIdParam]
  );

  const handleAddSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyIdParam || !newTitle.trim()) return;
    setSubmitting(true);
    setErrorMessage(null);
    const result = await sourcesApi.createSource(familyIdParam, {
      title: newTitle.trim(),
      type: newType as Parameters<typeof sourcesApi.createSource>[1]['type'],
      url: newUrl.trim() || undefined
    });
    setSubmitting(false);
    if (result.success) {
      setNewTitle('');
      setNewType('other');
      setNewUrl('');
      setShowAddForm(false);
      await loadSources();
    } else {
      setErrorMessage(
        !result.success && 'error' in result
          ? (result.error?.message ?? t('sources.addFailed'))
          : t('sources.addFailed')
      );
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (!familyIdParam || !window.confirm(tForms('confirm.deleteSource'))) return;
    setErrorMessage(null);
    const result = await sourcesApi.deleteSource(familyIdParam, sourceId);
    if (result.success) {
      setEditingSourceId(null);
      setExpandedSourceIds((prev) => {
        const next = { ...prev };
        delete next[sourceId];
        return next;
      });
      setCitationsBySource((prev) => {
        const next = { ...prev };
        delete next[sourceId];
        return next;
      });
      await loadSources();
    } else {
      setErrorMessage(result.error?.message ?? t('sources.deleteFailed'));
    }
  };

  const setSourceExpanded = useCallback(
    (sourceId: string, open: boolean) => {
      setExpandedSourceIds((prev) => ({ ...prev, [sourceId]: open }));
      if (open && citationsBySource[sourceId] === undefined) {
        void loadCitationsForSource(sourceId);
      }
    },
    [citationsBySource, loadCitationsForSource]
  );

  const startEditSource = (source: SourceResponse) => {
    setExpandedSourceIds((prev) => ({ ...prev, [source.id]: true }));
    setEditingSourceId(source.id);
    setEditTitle(source.title);
    setEditType(source.type as SourceType);
    setEditUrl(source.url ?? '');
  };

  const handleSaveEditSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyIdParam || !editingSourceId || !editTitle.trim()) return;
    setSubmittingEditSource(true);
    setErrorMessage(null);
    const result = await sourcesApi.updateSource(familyIdParam, editingSourceId, {
      title: editTitle.trim(),
      type: editType as Parameters<typeof sourcesApi.updateSource>[2]['type'],
      url: editUrl.trim() || undefined
    });
    setSubmittingEditSource(false);
    if (result.success) {
      setEditingSourceId(null);
      await loadSources();
    } else {
      setErrorMessage(result.error?.message ?? t('sources.updateFailed'));
    }
  };

  const startEditCitation = (c: CitationResponse, sourceId: string) => {
    setEditingCitationSourceId(sourceId);
    setEditingCitationId(c.id);
    setEditCitationDetailLocator(c.detailLocator ?? '');
    setEditCitationNotes(c.notes ?? '');
    setEditCitationConfidence((c.confidence as (typeof CONFIDENCE_OPTIONS)[number]) ?? '');
    setEditCitationMediaAssetId(c.mediaAssetId ?? '');
  };

  const handleSaveEditCitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyIdParam || !editingCitationId) return;
    setSubmittingEditCitation(true);
    setErrorMessage(null);
    const result = await citationsApi.updateCitation(familyIdParam, editingCitationId, {
      detailLocator: editCitationDetailLocator.trim() || undefined,
      notes: editCitationNotes.trim() || undefined,
      confidence: editCitationConfidence || undefined,
      mediaAssetId: editCitationMediaAssetId.trim() || undefined
    });
    setSubmittingEditCitation(false);
    if (result.success) {
      setEditingCitationId(null);
      setEditingCitationSourceId(null);
      if (editingCitationSourceId) {
        await loadCitationsForSource(editingCitationSourceId);
      }
    } else {
      setErrorMessage(result.error?.message ?? t('sources.updateCitationFailed'));
    }
  };

  const handleViewCitationImage = (mediaAssetId: string) => {
    setImageOverlayMediaId(mediaAssetId);
  };

  const handleChooseMediaSelect = useCallback(
    (mediaAssetId: string) => {
      if (chooseMediaContext === 'add') {
        setCitationMediaAssetId(mediaAssetId);
      } else {
        setEditCitationMediaAssetId(mediaAssetId);
      }
      setChooseMediaContext(null);
      loadMediaAssets();
    },
    [chooseMediaContext, loadMediaAssets]
  );

  const handleAddCitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyIdParam || !citationSourceId) return;
    setSubmittingCitation(true);
    setErrorMessage(null);
    const result = await sourcesApi.createCitation(familyIdParam, citationSourceId, {
      mediaAssetId: citationMediaAssetId.trim() || undefined,
      notes: citationNotes.trim() || undefined,
      detailLocator: citationDetailLocator.trim() || undefined,
      confidence: citationConfidence || undefined
    } as Parameters<typeof sourcesApi.createCitation>[2]);
    setSubmittingCitation(false);
    if (result.success) {
      setCitationNotes('');
      setCitationDetailLocator('');
      setCitationConfidence('');
      setCitationMediaAssetId('');
      setCitationSourceId(null);
      await loadCitationsForSource(citationSourceId);
    } else {
      setErrorMessage(result.error?.message ?? t('sources.addCitationFailed'));
    }
  };

  // Family-role fallback for the add-source action and for API builds that do not report per-source
  // access. Editing an individual source uses that source's own answer, since whoever added it may
  // correct it regardless of their family role.
  const canEdit = canModifyFamily(domainStore.currentFamily?.userRole);
  const familyDisplayName = domainStore.currentFamily?.displayName || domainStore.currentFamily?.name || 'Family';

  const countsByType = useMemo((): SourceTypeCounts => {
    const counts: SourceTypeCounts = {
      person: 0,
      website: 0,
      publication: 0,
      repository: 0,
      other: 0
    };
    for (const source of items) {
      counts[normalizeSourceType(source.type)] += 1;
    }
    return counts;
  }, [items]);

  const filteredItems = useMemo(() => {
    if (typeFilter === 'all') return items;
    return items.filter((source) => normalizeSourceType(source.type) === typeFilter);
  }, [items, typeFilter]);

  const filteredSourceIds = useMemo(() => new Set(filteredItems.map((s) => s.id)), [filteredItems]);

  const filteredCitationCount = useMemo(
    () =>
      Object.entries(citationsBySource).reduce(
        (sum, [sourceId, list]) => (filteredSourceIds.has(sourceId) ? sum + list.length : sum),
        0
      ),
    [citationsBySource, filteredSourceIds]
  );

  const resultsCountLabel = useMemo(() => {
    const n = typeFilter === 'all' ? total : filteredItems.length;
    if (typeFilter === 'all') {
      return `${n} source${n !== 1 ? 's' : ''}`;
    }
    const typeLabel = getSourceTypeLabel(typeFilter).toLowerCase();
    return `${n} ${typeLabel}${n !== 1 ? 's' : ''}`;
  }, [filteredItems.length, total, typeFilter]);

  const mediaLabelFor = (mediaAssetId: string): string | null => {
    if (!mediaAssetId) return null;
    return mediaAssets.find((a) => a.id === mediaAssetId)?.originalFilename ?? 'Selected image';
  };

  const openAddCitation = (sourceId: string) => {
    setExpandedSourceIds((prev) => ({ ...prev, [sourceId]: true }));
    setCitationSourceId((current) => (current === sourceId ? null : sourceId));
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      id: familyIdParam,
      name: domainStore.currentFamily.name,
      displayName,
      alternativeName,
      memberCount: domainStore.currentFamily.memberCount,
      createdAt: domainStore.currentFamily.createdAt,
      updatedAt: domainStore.currentFamily.updatedAt,
      createdBy: domainStore.currentFamily.createdBy ?? null,
      createdByNickname: domainStore.currentFamily.createdByNickname ?? null,
      updatedBy: domainStore.currentFamily.updatedBy ?? null,
      updatedByNickname: domainStore.currentFamily.updatedByNickname ?? null,
      userRole: domainStore.currentFamily.userRole ?? null
    });
  };

  const sourcesTour = useScreenTour({
    steps: FAMILY_SOURCES_TOUR_STEPS,
    completedStorageKey: FAMILY_SOURCES_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilySourcesTourSteps(steps, items.length > 0, canEdit)
  });

  if (errorMessage && !familyIdParam) {
    return (
      <div className="sources-page">
        <div className="sources-page__error" role="alert">
          <p>{errorMessage}</p>
          <button type="button" onClick={() => navigate('/families')} className="btn btn--ghost">
            {tForms('actions.backToFamilies')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sources-page">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('sources.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={sourcesTour.startTour}
        introTourId="sources-intro"
        intro={
          <>
            {t('sources.lede')} <ScreenHelpDot />
          </>
        }
        introTrailing={
          !isLoading && canEdit ? (
            <button
              type="button"
              className={`btn ${showAddForm ? 'btn--ghost' : 'btn--primary'} sources-page__add-btn`}
              data-tour="sources-add"
              onClick={() => setShowAddForm((s) => !s)}
              aria-expanded={showAddForm}
            >
              {!showAddForm && <Plus width={16} height={16} />}
              {showAddForm ? tCommon('cancel') : tForms('sources.addSource')}
            </button>
          ) : undefined
        }
      />

      {!isLoading && items.length > 0 && (
        <div data-tour="sources-controls" className="sources-page__control-row">
          <SourcesTypeFilter
            currentFilter={typeFilter}
            onFilterChange={setTypeFilter}
            countsByType={countsByType}
            totalCount={total}
          />
        </div>
      )}

      {showAddForm && (
        <form className="sources-form" onSubmit={handleAddSource}>
          <div className="sources-form__row">
            <label htmlFor="source-title" className="sources-form__label">
              {tForms('sources.titleRequired')}
            </label>
            <input
              id="source-title"
              type="text"
              className="sources-form__input"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t('sources.titlePlaceholder')}
              required
            />
          </div>
          <div className="sources-form__row">
            <label htmlFor="source-type" className="sources-form__label">
              {tForms('sources.type')}
            </label>
            <Select.Root value={newType} onValueChange={(v) => setNewType(v as (typeof SOURCE_TYPES)[number])}>
              <Select.Trigger
                id="source-type"
                className="form-select-trigger"
                aria-label={tForms('sources.sourceTypeAria')}
              >
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {SOURCE_TYPES.map((type) => (
                    <Select.Item key={type} value={type} className="form-select-item">
                      <Select.ItemText>{getSourceTypeLabel(type)}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
          <div className="sources-form__row">
            <label htmlFor="source-url" className="sources-form__label">
              {tForms('sources.urlOptional')}
            </label>
            <input
              id="source-url"
              type="url"
              className="sources-form__input"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder={t('sources.urlPlaceholder')}
            />
          </div>
          <div className="sources-form__actions">
            <button type="submit" className="btn btn--primary" disabled={submitting || !newTitle.trim()}>
              {submitting ? tForms('actions.adding') : tForms('sources.addSource')}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowAddForm(false)}>
              {tCommon('cancel')}
            </button>
          </div>
        </form>
      )}

      {errorMessage && (
        <div className="sources-page__error-inline">
          <p>{errorMessage}</p>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setErrorMessage(null)}>
            {tForms('actions.dismiss')}
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="sources-page__loading">
          <Spinner message={t('sources.loading')} />
        </div>
      ) : items.length === 0 ? (
        <div className="sources-page__empty">
          <p className="sources-page__empty-title">{t('sources.emptyTitle')}</p>
          <p className="sources-page__empty-body">{t('sources.emptyBody')}</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="sources-page__empty">
          <p className="sources-page__empty-title">{t('sources.noMatchTitle')}</p>
          <p className="sources-page__empty-body">
            {typeFilter !== 'all'
              ? t('sources.noMatchBody', { type: getSourceTypeLabel(typeFilter).toLowerCase() })
              : t('sources.noMatchBodyAll')}
          </p>
        </div>
      ) : (
        <>
          <ListResultsBar
            countLabel={
              <>
                {resultsCountLabel}
                {filteredCitationCount > 0 && (
                  <>
                    {' · '}
                    {tForms('sources.citationCount', { count: filteredCitationCount })}
                  </>
                )}
              </>
            }
          />
          <div className="sources-page__list" data-tour="sources-list">
            {filteredItems.map((source) => (
              <SourceCard
                key={source.id}
                familyId={familyIdParam!}
                source={source}
                canEdit={canModifyItem(source, canEdit)}
                isExpanded={expandedSourceIds[source.id] ?? false}
                onExpandedChange={(open) => setSourceExpanded(source.id, open)}
                citations={citationsBySource[source.id]}
                citationsLoading={
                  loadingCitations[source.id] ?? (citationsBySource[source.id] === undefined && citationsPreloadLoading)
                }
                isEditingSource={editingSourceId === source.id}
                editTitle={editTitle}
                editType={editType}
                editUrl={editUrl}
                submittingEditSource={submittingEditSource}
                onEditTitleChange={setEditTitle}
                onEditTypeChange={setEditType}
                onEditUrlChange={setEditUrl}
                onStartEditSource={() => startEditSource(source)}
                onCancelEditSource={() => setEditingSourceId(null)}
                onSaveEditSource={handleSaveEditSource}
                onDeleteSource={() => handleDeleteSource(source.id)}
                showAddCitationForm={citationSourceId === source.id}
                onToggleAddCitation={() => openAddCitation(source.id)}
                citationNotes={citationSourceId === source.id ? citationNotes : ''}
                citationDetailLocator={citationSourceId === source.id ? citationDetailLocator : ''}
                citationConfidence={citationSourceId === source.id ? citationConfidence : ''}
                submittingCitation={submittingCitation}
                onCitationNotesChange={setCitationNotes}
                onCitationDetailLocatorChange={setCitationDetailLocator}
                onCitationConfidenceChange={setCitationConfidence}
                onAddCitation={handleAddCitation}
                onChooseMediaForAdd={() => setChooseMediaContext('add')}
                mediaAssetLabel={citationSourceId === source.id ? mediaLabelFor(citationMediaAssetId) : null}
                editingCitationId={editingCitationSourceId === source.id ? editingCitationId : null}
                editCitationDetailLocator={editCitationDetailLocator}
                editCitationNotes={editCitationNotes}
                editCitationConfidence={editCitationConfidence}
                submittingEditCitation={submittingEditCitation}
                onEditCitationDetailLocatorChange={setEditCitationDetailLocator}
                onEditCitationNotesChange={setEditCitationNotes}
                onEditCitationConfidenceChange={setEditCitationConfidence}
                onStartEditCitation={(c) => startEditCitation(c, source.id)}
                onCancelEditCitation={() => {
                  setEditingCitationId(null);
                  setEditingCitationSourceId(null);
                }}
                onSaveEditCitation={handleSaveEditCitation}
                onChooseMediaForEdit={() => setChooseMediaContext('edit')}
                editMediaAssetLabel={mediaLabelFor(editCitationMediaAssetId)}
                onViewCitationImage={handleViewCitationImage}
              />
            ))}
          </div>
        </>
      )}

      {familyIdParam && domainStore.currentFamily && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={domainStore.currentFamily.displayName || domainStore.currentFamily.name}
          canonicalName={domainStore.currentFamily.name}
          hasAlternative={!!domainStore.currentFamily.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      {familyIdParam && (
        <ChooseMediaModal
          title={tForms('sources.chooseImage')}
          familyId={familyIdParam}
          isOpen={chooseMediaContext !== null}
          onClose={() => setChooseMediaContext(null)}
          onSelect={handleChooseMediaSelect}
          onUploadSuccess={() => loadMediaAssets()}
        />
      )}

      {familyIdParam && imageOverlayMediaId && (
        <ImageEnlargeOverlay
          isOpen
          onClose={() => setImageOverlayMediaId(null)}
          familyId={familyIdParam}
          mediaAssetId={imageOverlayMediaId}
        />
      )}

      <ScreenTourLayer tour={sourcesTour} />
    </div>
  );
});

export default FamilySourcesScreen;
